import React from 'react';

import { GoogleOAuthProvider } from '@react-oauth/google';
import type { AxiosError } from 'axios';
import PageTracker from 'components/PageTracker';
import ThemeProvider from 'context/theme';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter } from 'react-router-dom';
import Router from 'Router';

import config from './config';
import AuthProvider from './context/auth-context';
import LocalizationProvider from './context/localization-context';
import { MixpanelProvider } from './context/mixpanel-context';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 60 * 1000, // 1 minute
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount: number, error: unknown) => {
        const status = (error as AxiosError | undefined)?.response?.status;
        if (status === 401) return false;
        return failureCount < 1;
      },
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 3000),
    },
  },
});

// It is required to extend dayjs with customParseFormat plugin
// in order to parse dates with custom format
// https://v7.mantine.dev/dates/date-input/#date-parser
dayjs.extend(customParseFormat);

const App = (): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <MixpanelProvider>
              <GoogleOAuthProvider clientId={config.GOOGLEID || process.env.GOOGLEID || ''}>
                <LocalizationProvider>
                  <PageTracker />
                  <Router />
                </LocalizationProvider>
              </GoogleOAuthProvider>
            </MixpanelProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
      {config.SHOW_RQ_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} position='bottom-left' />}
    </QueryClientProvider>
  );
};

export default App;
